import { Paper } from '@material-ui/core';
import CustomButton from 'components/CustomInput/Button';
import CustomTab from 'components/CustomInput/Tab';
import useGeneralPostApprovalButton from 'hooks/useGeneralPostApprovalButton';
import React, { useState } from 'react';
import styled from 'styled-components';
import {
    REVIEWER,
    SECRETARY,
    IRB,
    TAB_CONSOLIDATION,
    DRAFT,
    FOR_REVIEW,
    TERMINATED,
    COMPLETED,
    REVIEWED,
    ACKNOWLEDGED,
    DISAPPROVED,
    APPROVED,
    TAB_REVIEWER_COMMENT,
    APPLICANT,
} from 'utility/lookup';
import GeneralCommentSheet from './GeneralCommentSheet';
import TextHeader from 'components/CustomText/TextHeader';
import { downloadLocalFile, postApprovalPermissionChecking } from 'utility/general';
import { PostApprovalFormType } from 'utility/postApprovalLookUp';
import { isArray } from 'lodash';
import LoadingIndicator from 'components/LoadingIndicator';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ConsolidationTabPage from 'components/ConsolidationTabPage';
import UploadWrapper from 'components/UploadWrapper';
import { SHyperLink } from 'style/CommonStyledComponent';
import { CHECK_CONSOLIDATION_UPDATE, GET_SIGNED_DOCUMENT } from 'store/key';
import useDialog from 'components/ConfirmDialog/useDialog';
import CancelIcon from '@material-ui/icons/Cancel';
import ReviewerComment from 'components/ReviewerComment';
import { SAVE_AMENDMENT_SUCCESS, APP_FORM_STATUS_NOT_MATCH } from 'utility/frontendMessage';
import api from 'api/call';

const SLayout = styled.div`
    padding: 10px;
    position: absolute;
    left: 0px;
    right: 0px;
    top: 55px;
    bottom: 35px;
    overflow: auto;
`;

const STabLayout = styled.div`
    position: absolute;
    bottom: 0px;
    height: 35px;
    left: 0px;
    right: 0px;
`;

const SHeaderLayout = styled.div`
    height: 55px;
    display: flex;
    padding: 0px 15px;
    align-items: center;
    background: #ecebf3;
    overflow: 'hidden';
    position: absolute;
    left: 0;
    top: 0;
    right: 0;
`;

const SMainLayout = styled.div`
    height: 100%;
    position: relative;
`;

const SCancelIcon = styled(CancelIcon)`
    width: 20px;
    height: 20px;
    cursor: pointer;
`;

const GeneralPostApprovalLayout = ({
    children,
    type, // type of form, ie. 1,2,3,4,5,6,7 4 = final report, 7 = progress report
    isFormCreate,
    onCreate = () => null,
    status = DRAFT, // default status = draft
    onSubmit,
    isLoading,
    isViewOnly,
    reportId,
    signedUpload, // in format of {fileName: "test.docx", key: 111}
    hasCCOAction, // required for display either submit / re-submit
    onSignedDocumentUploaded = (signedUpload) => null,
    checkIsFormDataChanged,
    checkSubmitFormValid,
    checkSaveFormValid,
    reSubmitCTCAndCTI,
    isPermission,
    siteInfo,
}) => {
    const dispatch = useDispatch();
    const showDialog = useDialog();
    const user = useSelector((state) => state.user);
    const [currentTab, setCurrentTab] = useState(PostApprovalFormType.FORM);

    const buttons = useGeneralPostApprovalButton({
        formTab: currentTab,
        formType: type,
        isFormCreate,
        onCreate,
        onSubmit,
        isViewOnly,
        status,
        reportId,
        hasCCOAction,
        switchToTab: (formTab) => {
            setCurrentTab(formTab);
        },
        user,
        currentTab,
        signedUpload,
        onSignedDocumentUploaded,
        checkIsFormDataChanged,
        checkSubmitFormValid,
        checkSaveFormValid,
        reSubmitCTCAndCTI,
        hasPermission: isPermission,
    });

    const defaultTabList = [
        {
            key: PostApprovalFormType.FORM,
            display: 'Form',
        },
    ];
    const [tabList, setTabList] = useState(defaultTabList);
    const [prevTabType, setPrevTabType] = useState(null);
    const [isButtonLoading, setButtonLoading] = useState(false);

    useEffect(() => {
        getTabList();
        // eslint-disable-next-line
    }, [status, siteInfo]);

    const getTabList = () => {
        const newList = defaultTabList;

        /** status > 0  && user.role !== REVIEWER to show comment sheet*/
        if (status > DRAFT && user.role !== REVIEWER) {
            newList.push({
                key: PostApprovalFormType.COMMENT,
                display: 'Comment Sheet',
            });
        }

        /** This tab will be shown in IRB / Secretary / Reviewer when CCO / Secretary confirms the document in confirmed, for review, reviewed, approved, acknowledged, disapproved, completed status.  */
        if (
            [FOR_REVIEW, REVIEWED, APPROVED, ACKNOWLEDGED, DISAPPROVED, COMPLETED].includes(status) &&
            [SECRETARY, IRB, REVIEWER].includes(user.role)
        ) {
            newList.push({
                key: TAB_REVIEWER_COMMENT,
                display: 'Reviewer Comment',
            });
        }

        /** show consolidationTabPage: */
        const showConsolidate = [
            FOR_REVIEW,
            TERMINATED,
            COMPLETED,
            REVIEWED,
            ACKNOWLEDGED,
            DISAPPROVED,
            APPROVED,
        ].includes(status);
        if (showConsolidate && [SECRETARY, IRB].includes(user.role)) {
            newList.push({ key: TAB_CONSOLIDATION, display: 'Consolidation' });
        }
        // 0 = initial application, 1 = amendment form, 5 = CTC, 6 = CTI
        let site = (type !== 0 && type !== 1 && type !== 5, type !== 6) ? (siteInfo ? ` (${siteInfo})` : null) : '';
        let formDisplay = site !== null ? `Form${site}` : 'Form (Study Info)';
        newList.map((list) => {
            if (list.key === PostApprovalFormType.FORM) {
                list.display = formDisplay;
            }
            return list;
        });

        setTabList(newList);
    };

    const getLayout = () => {
        if (currentTab === PostApprovalFormType.FORM) {
            return (
                <Paper>
                    <div style={{ padding: 15 }}>{children}</div>
                </Paper>
            );
        } else if (currentTab === PostApprovalFormType.COMMENT) {
            return <GeneralCommentSheet formType={type} reportId={reportId} />;
        } else if (currentTab === TAB_CONSOLIDATION) {
            const permission = postApprovalPermissionChecking({ currentTab, status });
            return <ConsolidationTabPage formId={reportId} isPermission={permission} documentType={type} />;
        } else if (currentTab === TAB_REVIEWER_COMMENT) {
            return <ReviewerComment formId={reportId} documentType={type} />;
        } else {
            return 'no specify';
        }
    };

    const changeTab = (key) => {
        setCurrentTab(key);
        setPrevTabType(key);
    };

    const handleTabButton = async (value) => {
        const { key } = value;
        if (prevTabType === TAB_CONSOLIDATION) {
            dispatch({
                type: CHECK_CONSOLIDATION_UPDATE,
                payload: {
                    formId: reportId,
                    documentType: type,
                    showDialog,
                    callback: async (isSaved) => {
                        if (isSaved) {
                            await showDialog({
                                description: SAVE_AMENDMENT_SUCCESS,
                                disableCancelButton: true,
                            });
                            changeTab(key);
                        } else {
                            changeTab(key);
                        }
                    },
                },
            });
            return;
        }

        changeTab(key);
    };

    const checkApplicationStatus = async () => {
        let data = {};
        let lastStatus = null;
        let res;
        data.docType = type;
        data.reportId = reportId;
        if (data.docType.toString() === '1') {
            res = await api.application.checkApplicationStatus(reportId);
            lastStatus = res.data.result;
        } else {
            res = await api.approval.getCTCAndCTIFile(data);
            if (res.data.result) {
                lastStatus = res.data.result.lastStatus;
            }
        }
        return lastStatus;
    };

    return (
        <>
            {isLoading ? (
                <div
                    style={{
                        width: '100%',
                        height: 500,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                    }}
                >
                    <LoadingIndicator />
                </div>
            ) : (
                <SMainLayout>
                    <SHeaderLayout>
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                width: '100%',
                            }}
                        >
                            <div style={{ display: 'flex' }}>
                                {buttons
                                    .filter((item) => {
                                        if (item.showInFormCreate && isFormCreate) {
                                            return true;
                                        }

                                        if (item.status === undefined) {
                                            return true;
                                        }

                                        // if status is array
                                        if (typeof item.status === 'object') {
                                            return item.status.includes(status);
                                        }

                                        return item.status === status;
                                    })
                                    .filter((item) => {
                                        if (item.condition !== undefined) {
                                            return item.condition({ type });
                                        }

                                        return true;
                                    })
                                    .filter((item) => {
                                        if (item.showInTab) {
                                            if (isArray(item.showInTab)) {
                                                return item.showInTab.includes(currentTab);
                                            }
                                            return currentTab === item.showInTab;
                                        }
                                        return true;
                                    })
                                    .filter((item) => {
                                        if (item.disableInForm) {
                                            if (isArray(item.disableInForm)) {
                                                return !item.disableInForm.includes(type);
                                            }
                                            return type !== item.showInTab;
                                        }
                                        return true;
                                    })
                                    .map((button, index) => {
                                        const extraProps = {};

                                        if (button.icon) {
                                            extraProps.startIcon = button.icon;
                                        }

                                        if (button.text === 'Download signed document') {
                                            const isNewUpload = signedUpload.isNewUpload;

                                            // const Wrapper = isNewUpload ? SDummy : SHyperLink;
                                            const downloadSignedFile = () => {
                                                if (!isNewUpload) {
                                                    dispatch({
                                                        type: GET_SIGNED_DOCUMENT,
                                                        payload: {
                                                            docType: type,
                                                            reportId,
                                                        },
                                                    });
                                                } else {
                                                    downloadLocalFile(signedUpload.file);
                                                }
                                            };

                                            return (
                                                <div
                                                    style={{ display: 'flex', alignItems: 'center' }}
                                                    key={`${button.text}-${index}`}
                                                >
                                                    {user.role === APPLICANT ? (
                                                        <>
                                                            Signed Document:
                                                            <SHyperLink
                                                                onClick={downloadSignedFile}
                                                                style={{ marginLeft: 5 }}
                                                            >
                                                                {button.fileName}
                                                            </SHyperLink>
                                                            {/* {button.key &&
                                                                postApprovalPermissionChecking({
                                                                    currentTab,
                                                                    status,
                                                                }) && (
                                                                    <SCancelIcon
                                                                        style={{ marginLeft: 10 }}
                                                                        onClick={() => {
                                                                            onSignedDocumentUploaded({
                                                                                fileName: '',
                                                                                key: null,
                                                                            });
                                                                        }}
                                                                    />
                                                                )} */}
                                                        </>
                                                    ) : (
                                                        <CustomButton
                                                            onClick={downloadSignedFile}
                                                            style={{ marginRight: 15 }}
                                                        >
                                                            Download signed document
                                                        </CustomButton>
                                                    )}
                                                </div>
                                            );
                                        } else if (button.text === 'Upload Signed Document') {
                                            return (
                                                <div style={{ paddingRight: 5 }} key={`${button.text}-${index}`}>
                                                    <UploadWrapper
                                                        multiple={false}
                                                        buttonText={'Upload signed document'}
                                                        onFileUploadFinish={(result) => button.action(result)}
                                                    />
                                                </div>
                                            );
                                        } else {
                                            return (
                                                <div key={`${button.text}-${index}`} style={{ marginRight: 15 }}>
                                                    {button && button.customDialog}
                                                    <CustomButton
                                                        {...extraProps}
                                                        loading={isButtonLoading}
                                                        onClick={async () => {
                                                            // check status is valid before execute the button action
                                                            setButtonLoading(true);

                                                            try {
                                                                if (!button.disableStatusChecking) {
                                                                    if (button.status) {
                                                                        let buttonStatus =
                                                                            typeof button.status !== 'object'
                                                                                ? [button.status]
                                                                                : button.status;
                                                                        if (reportId) {
                                                                            const remoteFormStatusId =
                                                                                await checkApplicationStatus();

                                                                            // if remote status updated and not match with the button's status requirement, block the button action.
                                                                            if (
                                                                                !buttonStatus.includes(
                                                                                    remoteFormStatusId,
                                                                                )
                                                                            ) {
                                                                                setButtonLoading(false);
                                                                                return showDialog({
                                                                                    description:
                                                                                        APP_FORM_STATUS_NOT_MATCH,
                                                                                    isError: true,
                                                                                    disableCancelButton: true,
                                                                                });
                                                                            }
                                                                        }
                                                                    }
                                                                }
                                                                button.action();
                                                            } catch (error) {
                                                                console.log(
                                                                    '101331 ApplicationActionBar.jsx error',
                                                                    button,
                                                                    error,
                                                                );
                                                            }
                                                            setButtonLoading(false);
                                                        }}
                                                        disabled={button.isDisabled?.()}
                                                        type={button.type}
                                                    >
                                                        {button.text}
                                                    </CustomButton>
                                                </div>
                                            );
                                        }
                                    })}
                            </div>
                            <TextHeader>{isViewOnly && 'View Only'}</TextHeader>
                        </div>
                    </SHeaderLayout>
                    <SLayout>
                        <div>{getLayout()}</div>
                    </SLayout>

                    <STabLayout>
                        <CustomTab data={tabList} value={currentTab} onClick={handleTabButton} />
                    </STabLayout>
                </SMainLayout>
            )}
        </>
    );
};

export default GeneralPostApprovalLayout;
